import React, { useState, useEffect } from 'react';
import axios from 'axios';
import backendURL from '../../config';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, CircularProgress, Alert, Box, TextField, IconButton, Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync'; // For the refresh button

const WithdrawalHistoryTable = ({ perPage = 10, showPagination = true, refreshKey }) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(perPage);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // --- NEW STATE: To track which specific row is being checked ---
  const [checkingId, setCheckingId] = useState(null);

  const token = localStorage.getItem("access_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setIsLoading(true);
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
      start_date: startDate || null,
      end_date: endDate || null,
    };
    axios.get(`${backendURL}/payments/withdrawals`, { params, headers })
      .then(res => {
        setWithdrawals(res.data.data);
        setTotalRows(res.data.total);
      })
      .catch(err => setError('Failed to fetch withdrawal history.'))
      .finally(() => setIsLoading(false));
  }, [page, rowsPerPage, startDate, endDate, refreshKey]);

  // --- NEW FUNCTION: To handle the API call for a single row ---
  const handleCheckStatus = async (transactionId) => {
    setCheckingId(transactionId); // Set loading state for this specific row
    try {
        const res = await axios.post(`${backendURL}/payments/withdrawals/${transactionId}/check-status`, {}, { headers });
        const newStatus = res.data.new_status;

        // Update the status of the specific row in the state immutably
        setWithdrawals(currentWithdrawals => 
            currentWithdrawals.map(w => 
                w.id === transactionId ? { ...w, status: newStatus } : w
            )
        );

    } catch (err) {
        alert(err.response?.data?.message || 'Failed to check status.');
    } finally {
        setCheckingId(null); // Reset loading state
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      {showPagination && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
      )}
      <Paper>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>S.no</TableCell>
                <TableCell>Datetime</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gateway</TableCell>
                <TableCell>Destination Account ID</TableCell>
                <TableCell>Gateway Payout ID</TableCell>
                {/* --- NEW COLUMN FOR ACTIONS --- */}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawals.map((row) => (
                <TableRow hover key={row.id}> {/* Use the unique 'id' as the key */}
                  <TableCell>{row.s_no}</TableCell>
                  <TableCell>{row.datetime}</TableCell>
                  <TableCell>${row.amount}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.gateway}</TableCell>
                  <TableCell>{row.destination_account_id}</TableCell>
                  <TableCell>{row.gateway_payout_id}</TableCell>
                  {/* --- NEW CELL with conditional rendering --- */}
                  <TableCell align="center">
                    {row.status === 'pending' && (
                      <Tooltip title="Check Status">
                        <IconButton
                          onClick={() => handleCheckStatus(row.id)}
                          disabled={checkingId === row.id}
                          size="small"
                        >
                          {checkingId === row.id ? <CircularProgress size={20} /> : <SyncIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {row.status === 'succeeded' && (
                       <CheckCircleIcon color="success" />
                    )}
                    {row.status === 'failed' && (
                       <ErrorIcon color="error" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {showPagination && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
};

export default WithdrawalHistoryTable;